import { AGE_RULES, computeAge } from "./age";
import {
  DATE_BOUND_MODES,
  DATE_BOUND_UNIT,
  FIELD_INPUT_TYPES,
  FIELD_VALIDATION_LIMITS,
  RELATIVE_TODAY,
  VALIDATION_MESSAGE_KEYS,
  VALIDATORS,
} from "./constants";
import { getPath, isBlank, isNil } from "./utils";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(?:\+?\d{1,3}|00\d{1,3})?\s?\d{9}$/;
const CITY_NAME_REGEX = /^[A-Za-zÀ-ÿ\s'-]+$/;
const CONTRACT_NUMBER_REGEX = /^[A-Za-z0-9-]+$/;

const IBAN_LENGTHS = {
  AT: 20,
  BE: 16,
  BG: 22,
  CH: 21,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  EE: 20,
  ES: 24,
  FI: 18,
  FR: 27,
  GB: 22,
  GI: 23,
  GR: 27,
  HR: 21,
  HU: 28,
  IE: 22,
  IS: 26,
  IT: 27,
  LT: 20,
  LU: 20,
  LV: 21,
  MT: 31,
  NL: 18,
  NO: 15,
  PL: 28,
  PT: 25,
  RO: 24,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  TR: 26,
  AD: 24,
  AE: 23,
};

const mod97 = (str) => {
  let remainder = 0;
  for (let i = 0; i < str.length; i++) {
    remainder = (remainder * 10 + Number(str[i])) % 97;
  }
  return remainder;
};

const isLetters2 = (value) => /^[A-Z]{2}$/.test(value);
const isAlnum = (value) => /^[A-Z0-9]+$/.test(value);

const normalizeIban = (input) => {
  if (input == null) return "";
  return String(input)
    .trim()
    .toUpperCase()
    .replace(/[\s\u00A0-]+/g, "");
};

const normalizeBic = (input) => {
  if (input == null) return "";
  return String(input)
    .trim()
    .toUpperCase()
    .replace(/[\s\u00A0-]+/g, "");
};

const alnumToDigits = (str) => {
  let out = "";
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code >= 48 && code <= 57) out += str[i];
    else out += String(code - 55);
  }
  return out;
};

const parseDate = (value) => {
  if (isBlank(value)) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    const parsed = new Date(text);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

export const DATE_BOUND_UNITS = [
  DATE_BOUND_UNIT.DAY,
  DATE_BOUND_UNIT.MONTH,
  DATE_BOUND_UNIT.YEAR,
];

/** Legacy string form kept readable for canvases saved before structured bounds. */
const LEGACY_RELATIVE_DATE = /^today([+-])(\d+)([dmy])$/i;

const LEGACY_UNITS = {
  d: DATE_BOUND_UNIT.DAY,
  m: DATE_BOUND_UNIT.MONTH,
  y: DATE_BOUND_UNIT.YEAR,
};

const toISODate = (date) => date.toISOString().slice(0, 10);

const startOfToday = (now) =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

const normalizeUnit = (unit) => {
  const text = String(unit || "").toLowerCase();
  if (text.startsWith("y")) return DATE_BOUND_UNIT.YEAR;
  if (text.startsWith("mo") || text === "m") return DATE_BOUND_UNIT.MONTH;
  return DATE_BOUND_UNIT.DAY;
};

export const TODAY_BOUND = {
  mode: DATE_BOUND_MODES.DURATION,
  amount: 0,
  unit: DATE_BOUND_UNIT.DAY,
};

export const normalizeDateBound = (raw) => {
  if (isBlank(raw)) return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime())
      ? null
      : { mode: DATE_BOUND_MODES.DATE, date: toISODate(raw) };
  }
  if (typeof raw === "object") {
    if (raw.mode === DATE_BOUND_MODES.DURATION) {
      const amount = Number(raw.amount);
      return {
        mode: DATE_BOUND_MODES.DURATION,
        amount: Number.isNaN(amount) ? 0 : amount,
        unit: normalizeUnit(raw.unit),
      };
    }
    const parsed = parseDate(raw.date);
    return parsed
      ? { mode: DATE_BOUND_MODES.DATE, date: toISODate(parsed) }
      : null;
  }
  const text = String(raw).trim();
  if (text.toLowerCase() === RELATIVE_TODAY) return { ...TODAY_BOUND };
  const match = text.match(LEGACY_RELATIVE_DATE);
  if (match) {
    const sign = match[1] === "-" ? -1 : 1;
    return {
      mode: DATE_BOUND_MODES.DURATION,
      amount: Number(match[2]) * sign,
      unit: LEGACY_UNITS[match[3].toLowerCase()],
    };
  }
  const parsed = parseDate(text);
  return parsed
    ? { mode: DATE_BOUND_MODES.DATE, date: toISODate(parsed) }
    : null;
};

export const resolveDateBound = (raw, now = new Date()) => {
  const bound = normalizeDateBound(raw);
  if (!bound) return null;
  if (bound.mode === DATE_BOUND_MODES.DATE) return parseDate(bound.date);
  const date = startOfToday(now);
  const amount = bound.amount || 0;
  if (bound.unit === DATE_BOUND_UNIT.DAY)
    date.setUTCDate(date.getUTCDate() + amount);
  if (bound.unit === DATE_BOUND_UNIT.MONTH)
    date.setUTCMonth(date.getUTCMonth() + amount);
  if (bound.unit === DATE_BOUND_UNIT.YEAR)
    date.setUTCFullYear(date.getUTCFullYear() + amount);
  return date;
};

const ok = () => ({ valid: true });
const fail = (reason) => ({ valid: false, reason });

export const DEFAULT_EFFECTIVE_DATE_ACCESSOR = "contract.issuanceDate";

export const validators = {
  [VALIDATORS.REQUIRED]: (value) =>
    isBlank(value) ? fail(VALIDATION_MESSAGE_KEYS.REQUIRED) : ok(),

  [VALIDATORS.REGEX]: (value, rule) => {
    if (isBlank(value)) return ok();
    try {
      const pattern = new RegExp(rule.pattern || "");
      return pattern.test(String(value))
        ? ok()
        : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
    } catch (_) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
    }
  },

  [VALIDATORS.MIN_LENGTH]: (value, rule) => {
    if (isBlank(value)) return ok();
    return String(value).length >= Number(rule.value)
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
  },

  [VALIDATORS.MAX_LENGTH]: (value, rule) => {
    if (isBlank(value)) return ok();
    return String(value).length <= Number(rule.value)
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
  },

  [VALIDATORS.MIN]: (value, rule) => {
    if (isBlank(value)) return ok();
    const number = Number(value);
    if (Number.isNaN(number)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_NUMBER);
    }
    return number >= Number(rule.value)
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
  },

  [VALIDATORS.MAX]: (value, rule) => {
    if (isBlank(value)) return ok();
    const number = Number(value);
    if (Number.isNaN(number)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_NUMBER);
    }
    return number <= Number(rule.value)
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
  },

  [VALIDATORS.EMAIL]: (value) => {
    if (isBlank(value)) return ok();
    const trimmed = String(value).trim();
    if (trimmed.length > FIELD_VALIDATION_LIMITS.EMAIL_MAX_LENGTH) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_EMAIL);
    }
    return EMAIL_REGEX.test(trimmed)
      ? ok()
      : fail(VALIDATION_MESSAGE_KEYS.INVALID_EMAIL);
  },

  [VALIDATORS.PHONE]: (value) => {
    if (isBlank(value)) return ok();
    const normalized = String(value).replace(/\s/g, "");
    return PHONE_REGEX.test(normalized)
      ? ok()
      : fail(VALIDATION_MESSAGE_KEYS.INVALID_PHONE);
  },

  [VALIDATORS.DATE_MIN]: (value, rule, _context, now) => {
    if (isBlank(value)) return ok();
    const date = parseDate(value);
    if (!date) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    const min = resolveDateBound(rule.value, now);
    if (!min) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    return date.getTime() >= min.getTime()
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_DATE);
  },

  [VALIDATORS.DATE_MAX]: (value, rule, _context, now) => {
    if (isBlank(value)) return ok();
    const date = parseDate(value);
    if (!date) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    const max = resolveDateBound(rule.value, now);
    if (!max) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    return date.getTime() <= max.getTime()
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_DATE);
  },

  [VALIDATORS.MIN_AGE]: (value, rule, context, now) => {
    if (isBlank(value)) return ok();
    const birth = parseDate(value);
    if (!birth) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    const years = Number(rule.value);
    if (Number.isNaN(years)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
    }
    const ageRule = rule.ageRule || AGE_RULES.AGE_ON_EFFECTIVE_DATE;
    const effectiveRaw = getPath(
      context,
      rule.effectiveDateAccessor || DEFAULT_EFFECTIVE_DATE_ACCESSOR,
    );
    const effective =
      parseDate(effectiveRaw) ||
      new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      );
    const age = computeAge(birth, effective, ageRule);
    if (age === null) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    return age >= years
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_DATE);
  },

  [VALIDATORS.MAX_AGE]: (value, rule, context, now) => {
    if (isBlank(value)) return ok();
    const birth = parseDate(value);
    if (!birth) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    const years = Number(rule.value);
    if (Number.isNaN(years)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_VALUE);
    }
    const ageRule = rule.ageRule || AGE_RULES.AGE_ON_EFFECTIVE_DATE;
    const effectiveRaw = getPath(
      context,
      rule.effectiveDateAccessor || DEFAULT_EFFECTIVE_DATE_ACCESSOR,
    );
    const effective =
      parseDate(effectiveRaw) ||
      new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      );
    const age = computeAge(birth, effective, ageRule);
    if (age === null) return fail(VALIDATION_MESSAGE_KEYS.INVALID_DATE);
    return age <= years
      ? ok()
      : fail(rule.messageKey || VALIDATION_MESSAGE_KEYS.INVALID_DATE);
  },

  [VALIDATORS.NIR]: (value) => {
    if (isBlank(value)) return ok();
    const raw = String(value);
    const s = raw.replace(/[\s.\-_/]/g, "").toUpperCase();
    if (s.length !== FIELD_VALIDATION_LIMITS.NIR_LENGTH) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_LENGTH);
    }
    if (!/^[1278]/.test(s)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_NUMBER);
    }
    const yearPart = s.slice(1, 3);
    if (!/^\d{2}$/.test(yearPart)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_NUMBER);
    }
    const monthPart = s.slice(3, 5);
    const monthNum = parseInt(monthPart, 10);
    if (!/^\d{2}$/.test(monthPart) || monthNum < 1 || monthNum > 12) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_NUMBER);
    }
    const keyStr = s.slice(13, 15);
    if (!/^\d{2}$/.test(keyStr)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_KEY);
    }
    const dep = s.slice(5, 7);
    let core = s.slice(0, 13);
    if (dep === "2A" || dep === "2B") {
      const depMapped = dep === "2A" ? "19" : "18";
      core = core.slice(0, 5) + depMapped + core.slice(7);
    } else if (!/^\d{2}$/.test(dep)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_DEPARTMENT_CODE);
    }
    if (!/^\d{13}$/.test(core)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_NUMBER);
    }
    const calcKey = (97 - mod97(core)).toString().padStart(2, "0");
    if (calcKey !== keyStr) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_SOCIAL_SECURITY_KEY);
    }
    return ok();
  },

  [VALIDATORS.IBAN]: (value) => {
    if (isBlank(value)) return ok();
    const iban = normalizeIban(value);
    if (iban.length < 4) {
      return fail(VALIDATION_MESSAGE_KEYS.IBAN_TOO_SHORT);
    }
    const country = iban.slice(0, 2);
    if (!isLetters2(country)) {
      return fail(VALIDATION_MESSAGE_KEYS.IBAN_BAD_COUNTRY_CODE);
    }
    if (!isAlnum(iban)) {
      return fail(VALIDATION_MESSAGE_KEYS.IBAN_BAD_CHARSET);
    }
    const expected = IBAN_LENGTHS[country];
    if (!expected) {
      return fail(VALIDATION_MESSAGE_KEYS.IBAN_UNSUPPORTED_COUNTRY);
    }
    if (iban.length !== expected) {
      return fail(VALIDATION_MESSAGE_KEYS.IBAN_BAD_LENGTH);
    }
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const remainder = mod97(alnumToDigits(rearranged));
    return remainder === 1
      ? ok()
      : fail(VALIDATION_MESSAGE_KEYS.IBAN_MODE97_FAILED);
  },

  [VALIDATORS.BIC]: (value) => {
    if (isBlank(value)) return ok();
    const bic = normalizeBic(value);
    if (
      bic.length !== FIELD_VALIDATION_LIMITS.BIC_LENGTH_SHORT &&
      bic.length !== FIELD_VALIDATION_LIMITS.BIC_LENGTH_LONG
    ) {
      return fail(VALIDATION_MESSAGE_KEYS.BIC_BAD_LENGTH);
    }
    if (!isAlnum(bic)) return fail(VALIDATION_MESSAGE_KEYS.BIC_BAD_CHARSET);
    if (!/^[A-Z]{4}/.test(bic)) {
      return fail(VALIDATION_MESSAGE_KEYS.BIC_BAD_BANK_CODE);
    }
    if (!isLetters2(bic.slice(4, 6))) {
      return fail(VALIDATION_MESSAGE_KEYS.BIC_BAD_COUNTRY_CODE);
    }
    if (!/^[A-Z0-9]{2}/.test(bic.slice(6, 8))) {
      return fail(VALIDATION_MESSAGE_KEYS.BIC_BAD_LOCATION);
    }
    if (
      bic.length === FIELD_VALIDATION_LIMITS.BIC_LENGTH_LONG &&
      !/^[A-Z0-9]{3}$/.test(bic.slice(8))
    ) {
      return fail(VALIDATION_MESSAGE_KEYS.BIC_BAD_BRANCH);
    }
    return ok();
  },

  [VALIDATORS.CITY_NAME]: (value) => {
    if (isBlank(value)) return ok();
    const text = String(value).trim();
    if (
      text.length < FIELD_VALIDATION_LIMITS.NAME_MIN_LENGTH ||
      text.length > FIELD_VALIDATION_LIMITS.NAME_MAX_LENGTH
    ) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    if (!CITY_NAME_REGEX.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    if (/\s{2,}/.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    if (/-{2,}/.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    if (/'{2,}/.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    if (/'[-]|-[']/.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    if (/^['-]|['-]$/.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CITY_NAME);
    }
    return ok();
  },

  [VALIDATORS.CONTRACT_NUMBER]: (value) => {
    if (isBlank(value)) return ok();
    const text = String(value).replace(/\s/g, "");
    if (!CONTRACT_NUMBER_REGEX.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CONTRACT_NUMBER);
    }
    if (/-{2,}/.test(text) || /^-+|-+$/.test(text)) {
      return fail(VALIDATION_MESSAGE_KEYS.INVALID_CONTRACT_NUMBER);
    }
    return ok();
  },
};

export const VALIDATOR_NAMES = Object.keys(validators);

export const VALIDATORS_BY_INPUT_TYPE = {
  [FIELD_INPUT_TYPES.TEXT]: [
    VALIDATORS.REQUIRED,
    VALIDATORS.REGEX,
    VALIDATORS.MIN_LENGTH,
    VALIDATORS.MAX_LENGTH,
    VALIDATORS.NIR,
    VALIDATORS.IBAN,
    VALIDATORS.BIC,
    VALIDATORS.CITY_NAME,
    VALIDATORS.CONTRACT_NUMBER,
  ],
  [FIELD_INPUT_TYPES.DATE]: [
    VALIDATORS.REQUIRED,
    VALIDATORS.DATE_MIN,
    VALIDATORS.DATE_MAX,
    VALIDATORS.MIN_AGE,
    VALIDATORS.MAX_AGE,
  ],
  [FIELD_INPUT_TYPES.NUMBER]: [
    VALIDATORS.REQUIRED,
    VALIDATORS.MIN,
    VALIDATORS.MAX,
  ],
  [FIELD_INPUT_TYPES.EMAIL]: [
    VALIDATORS.REQUIRED,
    VALIDATORS.EMAIL,
    VALIDATORS.REGEX,
    VALIDATORS.MAX_LENGTH,
  ],
  [FIELD_INPUT_TYPES.PHONE]: [
    VALIDATORS.REQUIRED,
    VALIDATORS.PHONE,
    VALIDATORS.REGEX,
  ],
};

export const getValidatorsForInputType = (type) => {
  const resolved =
    type === FIELD_INPUT_TYPES.SLIDER ? FIELD_INPUT_TYPES.NUMBER : type;
  return VALIDATORS_BY_INPUT_TYPE[resolved] || VALIDATOR_NAMES;
};

export const resolveMessage = (result, rule, messages = {}) => {
  if (!result || result.valid) return "";
  if (rule && rule.message) return rule.message;
  const key =
    (rule && rule.messageKey) ||
    result.reason ||
    VALIDATION_MESSAGE_KEYS.INVALID_VALUE;
  if (messages[key]) return messages[key];
  const nested = key.split(".").reduce((current, part) => {
    if (isNil(current) || typeof current !== "object") return undefined;
    return current[part];
  }, messages);
  if (typeof nested === "string") return nested;
  return messages[VALIDATION_MESSAGE_KEYS.INVALID_VALUE] || key;
};
